import { requestDeviceEnrolment } from "../app/deviceApproval.js";
import { desktopDeviceId } from "../app/deviceId.js";
import { isDesktop } from "../app/platform.js";
import { storedServerUrl } from "../app/serverUrl.js";
import { requestStepUp } from "../app/stepUp.js";
import { bearerToken } from "./token.js";

export class ApiClientError extends Error {
  public readonly status: number;
  /** The server's error code (e.g. TWO_FACTOR_SETUP_REQUIRED), when it sent one. */
  public readonly code: string | undefined;

  public constructor(status: number, message: string, code?: string | undefined) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
  }
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  readonly method?: HttpMethod | undefined;
  readonly body?: unknown;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function buildApiUrl(): string {
  const raw: unknown = import.meta.env?.["VITE_API_URL"];
  return typeof raw === "string" ? raw.replace(/\/$/, "") : "";
}

/** Desktop builds talk to the server the user picked; the browser build uses the build-time URL. */
export function apiBaseUrl(): string {
  if (isDesktop()) {
    return storedServerUrl() ?? buildApiUrl();
  }
  return buildApiUrl();
}

export type SignInBody =
  | { readonly client: "web" }
  | { readonly client: "desktop"; readonly tokenMode: "bearer"; readonly deviceId: string };

/**
 * Sign-in fields every login request carries: which app signs in, and on desktop a bearer
 * token instead of a cookie plus the install's device id (browsers get a device cookie).
 */
export function signInBody(): SignInBody {
  return isDesktop() ? { client: "desktop", tokenMode: "bearer", deviceId: desktopDeviceId() } : { client: "web" };
}

/** Keeps a token the server handed out in bearer mode. Cookie responses carry none. */
export function adoptToken(body: unknown): void {
  if (!isDesktop() || !isRecord(body)) {
    return;
  }
  const token = body["token"];
  if (typeof token === "string") {
    bearerToken.set(token);
  }
}

export async function parseOk(res: Response): Promise<unknown> {
  if (!res.ok) {
    let message = `Anfrage fehlgeschlagen (${res.status}).`;
    let code: string | undefined;
    try {
      const body: unknown = await res.json();
      if (isRecord(body) && isRecord(body["error"])) {
        if (typeof body["error"]["message"] === "string") {
          message = body["error"]["message"];
        }
        if (typeof body["error"]["code"] === "string") {
          code = body["error"]["code"];
        }
      }
    } catch {
      // Keep the generic message; the body is not JSON.
    }
    throw new ApiClientError(res.status, message, code);
  }
  const data: unknown = await res.json();
  return data;
}

export async function requestJson(path: string, options: RequestOptions = {}): Promise<unknown> {
  try {
    return await sendJson(path, options);
  } catch (err: unknown) {
    // A sensitive change needs a fresh password or passkey check: confirm, then retry once.
    if (err instanceof ApiClientError && err.code === "REAUTH_REQUIRED" && (await requestStepUp())) {
      return sendJson(path, options);
    }
    // Admin recovery and similar actions accept only a passkey with user verification.
    if (err instanceof ApiClientError && err.code === "PASSKEY_REAUTH_REQUIRED" && (await requestStepUp("passkey"))) {
      return sendJson(path, options);
    }
    // A sign-in on a new device waits until a passkey or a trusted device enrols it.
    if (err instanceof ApiClientError && err.code === "DEVICE_APPROVAL_REQUIRED" && (await requestDeviceEnrolment())) {
      return sendJson(path, options);
    }
    throw err;
  }
}

async function sendJson(path: string, options: RequestOptions): Promise<unknown> {
  const desktop = isDesktop();
  const headers: Record<string, string> = {};
  const token = desktop ? bearerToken.get() : undefined;
  if (token !== undefined) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const init: RequestInit = {
    method: options.method ?? "GET",
    credentials: desktop ? "omit" : "include",
    headers
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  const res = await fetch(`${apiBaseUrl()}${path}`, init);
  return parseOk(res);
}
